import { Button } from "@/components/Button";
import { NAVIGATE_BACKWARDS } from "@/utils/const";
import useIsMobile from "@/utils/useIsMobile";
import { faCaretLeft } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "flex h-screen w-screen to-white pt-onboarding-header-height-mobile md:bg-gradient-to-r md:pt-onboarding-header-height",
    sizingContainer: "max-w-lg flex-grow 2xl:mx-auto",
    backButtonContainer:
      "mx-auto flex flex-grow items-end bg-white pb-30 text-secondary md:mx-[unset] md:mt-30 md:bg-transparent md:pb-50",
    doublePanelContainer:
      "flex h-full w-full flex-col bg-white md:flex-row md:bg-transparent",
    leftPanelContainer:
      "no-scrollbar flex w-full flex-col overflow-y-visible p-20 pb-40 md:overflow-y-scroll md:pb-130 md:pl-130 md:pr-40 md:pt-50",
    rightPanelContainer:
      "no-scrollbar flex w-full flex-col overflow-y-visible p-20 pb-40 md:overflow-y-scroll md:pl-40 md:pr-130 md:pt-50",
    contentContainer:
      "no-scrollbar flex h-[unset] flex-col overflow-y-scroll md:h-full",
    rowContainer: "flex flex-row",
    contentPanelContainer:
      "flex items-center justify-center bg-white p-20 md:bg-transparent md:px-130 md:py-0",
  },
  variants: {
    backgroundSplit: {
      even: {
        base: "from-50% to-50%",
        leftPanelContainer: "md:w-[50%]",
        rightPanelContainer: "md:w-[50%]",
      },
      leftSkewed: {
        base: "from-55% to-45%",
        leftPanelContainer: "md:w-[55%]",
        rightPanelContainer: "md:w-[45%]",
      },
    },
    backgroundColor: {
      dark: {
        base: "from-primary-dark",
        leftPanelContainer: "bg-primary-dark md:bg-transparent",
      },
      light: {
        base: "from-gray-light",
        leftPanelContainer: "bg-gray-light",
      },
    },
    hasCenterPanel: {
      true: {
        leftPanelContainer: "md:pb-80",
        rightPanelContainer: "md:pb-80",
        backButtonContainer: "px-130",
        rowContainer: "flex-col sm:flex-row",
      },
      false: {},
    },
  },
  defaultVariants: {
    backgroundSplit: "even",
    backgroundColor: "light",
    hasCenterPanel: false,
  },
});

interface IProps {
  isDark?: boolean;
  leftPanel: JSX.Element;
  rightPanel: JSX.Element;
  centerPanel?: JSX.Element;
  backgroundSplit?: VariantProps<typeof styles>["backgroundSplit"];
  backButtonPath?: string;
}

const SplitLayout = ({
  isDark,
  leftPanel,
  rightPanel,
  centerPanel,
  backgroundSplit,
  backButtonPath,
}: IProps) => {
  const { t } = useTranslation();

  const {
    base,
    sizingContainer,
    backButtonContainer,
    doublePanelContainer,
    leftPanelContainer,
    rightPanelContainer,
    contentContainer,
    rowContainer,
    contentPanelContainer,
  } = styles({
    backgroundColor: isDark ? "dark" : "light",
    backgroundSplit: backgroundSplit,
    hasCenterPanel: !!centerPanel,
  });
  const isMobile = useIsMobile();

  const renderBackButton = () => {
    if (!backButtonPath) return null;

    return (
      <div className={backButtonContainer()}>
        <div>
          <Button
            as="link"
            icon={faCaretLeft}
            iconPosition="start"
            iconClassName="h-20 w-20"
            variant="link"
            to={backButtonPath}
            state={{
              direction: NAVIGATE_BACKWARDS,
            }}
            testId="back-button">
            {t("layouts.splitLayout.backButton")}
          </Button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (centerPanel)
      return (
        <div className={contentContainer()}>
          <div className={rowContainer()}>
            <div className={leftPanelContainer()}>
              {leftPanel}
              {!centerPanel && !isMobile && renderBackButton()}
            </div>
            <div className={rightPanelContainer()}>{rightPanel}</div>
          </div>
          <div className={contentPanelContainer()}>{centerPanel}</div>
          {(!!centerPanel || !!isMobile) && renderBackButton()}
        </div>
      );

    return (
      <div className={doublePanelContainer()}>
        <div className={leftPanelContainer()}>
          {leftPanel}
          {!centerPanel && !isMobile && renderBackButton()}
        </div>
        <div className={rightPanelContainer()}>{rightPanel}</div>
        {!centerPanel && !!isMobile && renderBackButton()}
      </div>
    );
  };

  return (
    <div className={base()}>
      <div className={sizingContainer()}>{renderContent()}</div>
    </div>
  );
};

export default SplitLayout;
